import React from 'react';

import { SidePage, Section } from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';
import Permissions from '@gogame/permissions';

import { useIsUpdating } from 'scenes/CustomAudiences/LookalikeAudiences/data';
import PartnerAudienceDetails from 'scenes/CustomAudiences/PartnerAudiences/components/PartnerAudienceDetails';
import { TAB_DETAILS, TAB_ACTIONS_LOG } from 'scenes/CustomAudiences/constants';

import { ACTION_SHARE } from '../../constants';
import {
  useLoadLookalikeAudienceDetails,
  useShareLookalikeAudience,
  useLoadSharedAccounts,
} from '../../data';
import LAActions from '../Actions';
import ActionsLog from './ActionsLog';
import General from './General';
import Metadata from './Metadata';
import PartnerAudience from './PartnerAudience';
import SharedAccounts from './SharedAccounts';

const LookalikeAudienceDetails = ({
  id,
  onClose,
  onExited,
  open,
  activeTabId = TAB_DETAILS,
}) => {
  const { data, isLoading, isRefetching } = useLoadLookalikeAudienceDetails(id);
  const { data: sharedAccountsData = [], isLoading: isLoadingSharedAccounts } =
    useLoadSharedAccounts(id);
  const isUpdating = useIsUpdating();

  const [childComponent, openChildComponent] = useChildComponent();

  const [shareLookalikeAudience] = useShareLookalikeAudience({
    showModal: openChildComponent,
  });

  const handleOpenPartnerAudience = (_e, { id: partnerAudienceId }) => {
    openChildComponent(PartnerAudienceDetails, {
      id: partnerAudienceId,
    });
  };

  const handleSharedAccount = async () => {
    shareLookalikeAudience(data);
  };

  return (
    <SidePage
      backdrop={false}
      onClose={onClose}
      onExited={onExited}
      loading={isLoading || isUpdating}
      fetching={isRefetching || isLoadingSharedAccounts}
      open={open}
      size={'lg'}
    >
      <SidePage.Title
        title={'Lookalike Audience'}
        subTitle={data && data.name}
        entityId={id}
      />
      <SidePage.Tabs activeTabId={activeTabId}>
        <SidePage.Tab label={TAB_DETAILS}>
          <SidePage.NewBody wrapper={true} dense={true}>
            <SidePage.NewContent withSidebar={true}>
              <Section title={'General'}>
                <General data={data} />
              </Section>

              <Permissions actions={data?.actions}>
                {({ isActionAllowed }) => {
                  if (isActionAllowed(ACTION_SHARE)) {
                    return (
                      <SharedAccounts
                        id={id}
                        data={sharedAccountsData}
                        onShare={handleSharedAccount}
                      />
                    );
                  }
                }}
              </Permissions>

              {data?.partnerAudience ? (
                <PartnerAudience
                  data={data}
                  onOpenPartnerAudience={handleOpenPartnerAudience}
                />
              ) : null}
            </SidePage.NewContent>
            <SidePage.Sidebar
              actions={
                <LAActions
                  actions={data?.actions}
                  onDelete={onClose}
                  data={data}
                  withShare={false}
                />
              }
            >
              <Metadata data={data} />
            </SidePage.Sidebar>
          </SidePage.NewBody>
        </SidePage.Tab>
        <SidePage.Tab label={TAB_ACTIONS_LOG}>
          <SidePage.NewBody>
            <ActionsLog id={id} />
          </SidePage.NewBody>
        </SidePage.Tab>
      </SidePage.Tabs>
      <SidePage.Actions>
        <SidePage.CloseButton />
      </SidePage.Actions>
      {childComponent}
    </SidePage>
  );
};
export default LookalikeAudienceDetails;
